import { useEffect } from 'react';

/**
 * 防止 iOS/微信 WebView 中滚动容器的"橡皮筋回弹"效果
 * 在 iOS Safari 和微信 WebView 中，当滚动容器滚动到顶部继续下拉或滚动到底部继续上拉时，
 * 会出现"橡皮筋回弹"效果（整页跟手滑动），影响用户体验。该 Hook 通过监听触摸事件，
 * 在滚动边界阻止默认行为，从而禁用该效果。
 * 
 * @param {string} elementId - 需要处理滚动容器的 DOM 元素 ID
 * @param {boolean} [enabled=true] - 是否启用该功能，默认为 true
 * 
 * @example
 * // 在组件中使用
 * function MyComponent() {
 *   usePreventTouchRubberBand('scroll-container', true);
 *   return <div id="scroll-container">滚动内容</div>;
 * }
 */
export function usePreventTouchRubberBand(elementId: string, enabled: boolean = true) {
  useEffect(() => {
    if (typeof document === 'undefined') return;
    
    // 获取目标滚动容器元素
    const root = document.getElementById(elementId);
    
    // 如果未启用或元素不存在，则不进行任何处理
    if (!enabled || !root) return;

    // 记录触摸开始时的 Y 坐标
    let startY = 0;
    
    /**
     * 触摸开始事件处理函数
     * 记录初始触摸位置的 Y 坐标，用于计算后续的滑动方向
     */
    const onTouchStart = (e: TouchEvent) => {
      // 获取第一个触摸点的 Y 坐标，如果不存在则默认为 0
      startY = e.touches?.[0]?.clientY ?? 0;
    };
    
    /**
     * 触摸移动事件处理函数
     * 检测滚动边界情况，并在需要时阻止默认的回弹行为
     */
    const onTouchMove = (e: TouchEvent) => {
      // 获取当前触摸点的 Y 坐标
      const currentY = e.touches?.[0]?.clientY ?? 0;
      
      // 计算 Y 方向的移动距离
      // deltaY > 0 表示向下滑动（下拉），deltaY < 0 表示向上滑动（上拉）
      const deltaY = currentY - startY;

      const el = root!;
      
      // 当前滚动位置：从顶部滚动的距离
      const scrollTop = el.scrollTop;
      
      // 最大滚动位置：总内容高度 - 可视区域高度
      const maxScrollTop = el.scrollHeight - el.clientHeight;

      // 判断是否处于滚动边界且继续向边界外滑动
      // 情况1：已在顶部（scrollTop <= 0）且继续下拉（deltaY > 0）
      // 情况2：已在底部（scrollTop >= maxScrollTop）且继续上拉（deltaY < 0）
      // 满足以上任一情况时，阻止默认行为以避免橡皮筋回弹效果
      if ((scrollTop <= 0 && deltaY > 0) || (scrollTop >= maxScrollTop && deltaY < 0)) {
        e.preventDefault();
      }
    };

    // 添加事件监听器
    // touchstart 使用 passive:true 以提高滚动性能（因为该事件不需要阻止默认行为）
    root.addEventListener('touchstart', onTouchStart, { passive: true });
    
    // touchmove 必须使用 passive:false，因为需要调用 preventDefault() 来阻止回弹
    // 注意：passive:false 可能会轻微影响滚动性能，但这是必需的权衡
    root.addEventListener('touchmove', onTouchMove, { passive: false });

    // 清理函数：组件卸载或依赖项变化时移除事件监听器
    return () => {
      root.removeEventListener('touchstart', onTouchStart);
      root.removeEventListener('touchmove', onTouchMove);
    };
  }, [elementId, enabled]); // 当 elementId 或 enabled 变化时重新设置事件监听
}


